import React from 'react';
import { Box, Container, Typography, Chip, Card, CardContent, Divider } from '@mui/material';
import ConstructionBanner from '../components/ConstructionBanner';
import Navbar from '../components/Navbar';

// ── Coming soon features — priority order ─────────────────────────────────
const COMING = [
  {
    label: 'Situational Play Explorer',
    emoji: '🔍',
    color: '#2ea84b',
    when: 'Next up',
    desc: 'The most powerful tool we\'re building. Filter any play situation and instantly see how teams and players perform in that exact context.',
    items: [
      'Filter by down, distance, quarter, field zone, or formation',
      'Instantly see EPA, success rate, yards/play, and conversion % for that slice',
      'Compare how teams attack the same situation differently',
      '3rd & long: which QBs are best from shotgun under pressure?',
      'Red zone run/pass tendencies broken down by hash and personnel',
      'Save custom filters for your favorite matchup scenarios',
    ],
  },
  {
    label: 'Player Comparison Tool',
    emoji: '⚖️',
    color: '#3b82f6',
    when: 'Next up',
    desc: 'Side-by-side player breakdowns built for real analysis — not just box scores.',
    items: [
      'Compare any two skill players on EPA/play, success rate, and air yards',
      'Deep ball % — completions and EPA on throws 20+ yards downfield',
      'Target share, WOPR, and YAC compared on a single screen',
      'Radar chart overlays to visualize multi-dimensional skill profiles',
      'Historical season-over-season comparison for the same player',
    ],
  },
  {
    label: 'Theory Tester',
    emoji: '⚗️',
    color: '#a855f7',
    when: 'Next up',
    desc: 'Type a football hypothesis in plain English. We find the data and tell you if you\'re right.',
    items: [
      '"Shotgun runs are actually bad" → pulls all run plays from shotgun, shows EPA vs. league average',
      '"Teams pass more on 1st down when trailing" → game script + play type breakdown, all 32 teams',
      '"4th & 1 conversion rate is higher than people think" → full historical rate with team rankings',
      '"Mahomes is better under pressure than any QB" → pressure split EPA comparison across all QBs',
      'Guided Situational Explorer under the hood — Theory Tester just builds the filters for you',
      'Submit your own theories — top submissions get featured in the weekly Nerd Report',
    ],
  },
  {
    label: 'The Nerd Report',
    emoji: '📰',
    color: '#f97316',
    when: 'Coming soon',
    desc: 'Auto-generated weekly analytics digest published every Tuesday. Data-first, no filler, built for sharing.',
    items: [
      'Top 5 offenses and defenses by EPA/play from the previous week',
      'Biggest efficiency swings — who overperformed or cratered vs. their season average',
      'Explosive play leaders and who came back down to earth',
      'Situational spotlight — one deep-dive each week (e.g. "3rd & medium from the shotgun in Q4")',
      'Theory of the Week — one reader-submitted hypothesis tested against real data',
      'Social-ready charts optimized for Twitter/Reddit — analytics communities eat this up',
    ],
  },
  {
    label: 'Team Style Profiles & Playcalling Tendencies',
    emoji: '📐',
    color: '#f5a623',
    when: 'Coming soon',
    desc: 'Automatically classify every team\'s offensive and defensive identity using real play-by-play data.',
    items: [
      'Team identity tags — Run Heavy, Pass Aggressive, Play Action Heavy, RPO-based',
      'Run vs. pass rate by down and distance — every team, every situation',
      'Early down efficiency — how well teams stay ahead of the chains on 1st & 2nd down',
      'Pass/run tendency by field zone — own territory vs. opponent territory vs. red zone',
      '3rd and short run % — who punches it in vs. who throws?',
      'Playcalling shifts by game script — ahead vs. behind tendencies',
    ],
  },
  {
    label: 'Win Probability Model',
    emoji: '📈',
    color: '#8b5cf6',
    when: 'Coming soon',
    desc: 'Visualize how win probability shifted throughout any game — built on our own PBP model.',
    items: [
      'Win probability graph for any game — play-by-play throughout 60 minutes',
      'Identify the swing play — the single moment that changed the game',
      'Season-long clutch index — which QBs improve win probability most in high-leverage plays',
      'Leverage index per play — how much each snap actually mattered',
      'Fourth quarter comeback frequency and EPA-in-close-games breakdown',
    ],
  },
  {
    label: 'Advanced Team Analytics',
    emoji: '🏈',
    color: '#f5a623',
    when: 'Coming soon',
    desc: 'Deeper team-level metrics beyond what\'s already in the stats hub.',
    items: [
      'Game script EPA splits — how teams perform while ahead vs. behind',
      'Half-by-half splits — first half, second half, and fourth quarter EPA',
      'Coverage shell tendencies — Cover-1 through Cover-6 rates per team',
      'Pass-rush win rates — edge and interior linemen by alignment',
      'Formation tendencies — 11/12/21/22 personnel usage and EPA per package',
      'Drive efficiency: TD%, punt%, turnover% per drive across all 32 teams',
    ],
  },
  {
    label: 'Weekly Analytics Reports',
    emoji: '📰',
    color: '#f97316',
    when: 'Coming soon',
    desc: 'Automated weekly insights published every Tuesday — shareable, data-first, no filler.',
    items: [
      'Top 5 offenses and defenses by EPA/play from the previous week',
      'Biggest positive and negative efficiency swings week-over-week',
      'Explosive play leaders — who hit their ceiling and who came back to earth',
      'Situational spotlight — one deep-dive each week on a specific filter (e.g. 3rd & medium, shotgun vs. pressure)',
      'Social-ready charts and graphics optimized for Twitter/Reddit sharing',
    ],
  },
  {
    label: 'Fantasy Tools',
    emoji: '🏆',
    color: '#ec4899',
    when: 'Spring 2026',
    items: [
      'Fantasy projections — Nerd Rating × snap share × target share = smarter starts',
      'Start/sit optimizer — head-to-head matchup analysis with EPA and usage context',
      'Waiver wire ranker — opportunity-based value scoring with trend indicators',
      'DFS value ratings — salary-adjusted NR scores for GPP and cash lineups',
    ],
  },
  {
    label: 'College Football Analytics',
    emoji: '🎓',
    color: '#22d3ee',
    when: 'Spring 2026',
    items: [
      'College Nerd Rating — the NR formula applied to FBS players',
      'Team EPA by conference — which CFP contenders are efficient vs. padding stats',
      'Player stat leaders — QB, RB, WR, TE across all major FBS conferences',
      'NFL Draft scouting page — PBP-based prospect metrics to surface overlooked talent',
    ],
  },
  {
    label: 'Tracking & NGS Data',
    emoji: '📡',
    color: '#ef4444',
    when: 'Requires data deal',
    items: [
      'Receiver separation metrics from NFL Next Gen Stats',
      'Quarterback pocket movement and time-to-throw profiles',
      'Route tree analysis — separation and EPA by route type for every receiver',
      'Defensive back coverage grades by scheme and alignment',
      'Speed and acceleration profiles for skill positions',
    ],
  },
];

const WHEN_COLORS = {
  'Next up':            { bg: '#0d2b1a', color: '#2ea84b',  border: '#2ea84b55' },
  'Coming soon':        { bg: '#2a1a05', color: '#f5a623',  border: '#f5a62355' },
  'Spring 2026':        { bg: '#1a1040', color: '#8b5cf6',  border: '#8b5cf655' },
  'Requires data deal': { bg: '#2a0d0d', color: '#ef4444',  border: '#ef444455' },
};

export default function InTheWorksPage() {
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent' }}>
      <ConstructionBanner />
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{
          mb: 5, pt: 2, pb: 3,
          borderBottom: '1px solid rgba(26,46,68,0.5)',
        }}>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>🧠 Brainstorm Lab</Typography>
          <Typography sx={{ color: '#7a9ab8', maxWidth: 660, lineHeight: 1.75, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>
            This is where the ideas live. Features we're building, tools we're dreaming up,
            and experiments we want to run — in priority order. The foundation is solid;
            everything here makes it nerdier.
          </Typography>
        </Box>

        {/* Feature cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 5 }}>
          {COMING.map((section, i) => {
            const wc = WHEN_COLORS[section.when] || WHEN_COLORS['Coming soon'];
            return (
              <Card key={section.label} sx={{
                background: 'linear-gradient(135deg, rgba(10,20,36,0.82) 0%, rgba(6,14,26,0.82) 100%)',
                border: `1px solid ${section.color}2e`,
                borderRadius: 2,
                backdropFilter: 'blur(8px)',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': { borderColor: `${section.color}55`, boxShadow: `0 4px 20px ${section.color}0f` },
              }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  {/* Title row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: section.desc ? 1 : 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1.35rem', lineHeight: 1 }}>{section.emoji}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: section.color }}>
                      {section.label}
                    </Typography>
                    <Chip label={section.when} size="small" sx={{
                      fontWeight: 700, fontSize: '0.65rem', height: 20,
                      bgcolor: wc.bg, color: wc.color, border: `1px solid ${wc.border}`,
                    }} />
                    <Chip label={`#${i + 1} priority`} size="small" sx={{
                      fontWeight: 600, fontSize: '0.6rem', height: 18,
                      bgcolor: 'rgba(74,112,150,0.15)', color: '#6a8aa8',
                    }} />
                  </Box>

                  {/* Optional subtitle */}
                  {section.desc && (
                    <Typography sx={{ fontSize: '0.8rem', color: '#7a9ab8', lineHeight: 1.55, mb: 1.5, maxWidth: 680 }}>
                      {section.desc}
                    </Typography>
                  )}

                  {/* Items */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                    {section.items.map((item, j) => (
                      <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: section.color,
                          flexShrink: 0, mt: '7px' }} />
                        <Typography sx={{ fontSize: '0.83rem', color: '#9eb8d0', lineHeight: 1.55 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', my: 4 }} />

        {/* Metrics we already track — reference table */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>📊 Metrics Glossary Preview</Typography>
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.82rem', mb: 2.5 }}>
          Every stat we track today — free tier and Nerd+. Full definitions in the Glossary tab.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: 1.5, mb: 5 }}>
          {[
            { group: 'Passing',    free: ['GP','CMP','ATT','PCT','YDS','YPG','TD','INT','INT%','YPA','ANY/A','RTG','QBR','SCK'],   premium: ['EPA/ATT','SR%','EXPL%','PRES%','SHOT%','DAKOTA','aYd%'] },
            { group: 'Rushing',    free: ['GP','CAR','YDS','YPG','YPC','TD','FUM','SNAP%','FPTS/G'],                                premium: ['EPA/CAR','EPA/TGT','SR%','EXPL%'] },
            { group: 'Receiving',  free: ['GP','TGT','REC','CTCH%','YDS','YPG','YPT','YPR','TD','20+','SNAP%','FPTS/G'],           premium: ['EPA/TGT','aDOT','YAC','TGT%','WOPR','RACR','SR%','EXPL%'] },
            { group: 'Defensive',  free: ['GP','NR','TOT','SOLO','TFL','SCK','QBH','HRY','INT','FF','PD'],                          premium: ['NR Score breakdown'] },
            { group: 'Team — Basic',  free: ['W','L','T','PF','PA','Diff','PF/G','PA/G','Pass Yds/G','Rush Yds/G','Total Yds/G','Sacks+','Sacks−','TO Diff','1st Dwns/G'], premium: [] },
            { group: 'Team — Advanced', free: ['Off EPA/Play','Def EPA/Play'],                                                      premium: ['Off SR%','Def SR%','Off EXPL%','Def EXPL%','CPOE','Pres Rate','RZ TD%','3rd Conv%'] },
          ].map(g => (
            <Box key={g.group} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(10,20,34,0.6)', border: '1px solid #2a4560' }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#e8f0f8', mb: 1 }}>{g.group}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: g.premium.length ? 0.75 : 0 }}>
                {g.free.map(s => (
                  <Chip key={s} label={s} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600,
                    bgcolor: 'rgba(46,168,75,0.12)', color: '#2ea84b', border: '1px solid #2ea84b33' }} />
                ))}
              </Box>
              {g.premium.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {g.premium.map(s => (
                    <Chip key={s} label={s} size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600,
                      bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid #f5a62333' }} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', my: 4 }} />

        {/* Data sources */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>📡 Data Sources</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 5 }}>
          {[
            { name: 'ESPN Public API',       type: 'Free',    detail: 'Live rosters, schedules, scores, news, basic stats',                    live: true },
            { name: 'nflverse player_stats', type: 'Free',    detail: 'Season EPA, CPOE, air yards, target share, snap counts, fantasy pts',    live: true },
            { name: 'nflverse PBP',          type: 'Free',    detail: 'Full play-by-play: EPA, success rate, explosive rate, RZ, pressure rate',live: true },
            { name: 'nflverse rosters',      type: 'Free',    detail: 'Player age, college, draft pick, years of experience',                   live: true },
            { name: 'NFL Next Gen Stats',    type: 'Limited', detail: 'Tracking data (separation, speed, route trees) — limited public access',  live: false },
            { name: 'SportsDataIO / Sportradar', type: 'Paid', detail: 'Real-time live game data, projections, betting lines',                  live: false },
          ].map(src => (
            <Box key={src.name} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(10,20,34,0.6)',
              border: '1px solid #2a4560', display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Chip label={src.type} size="small" sx={{
                flexShrink: 0, fontSize: '0.6rem', height: 18, fontWeight: 700,
                bgcolor: src.type === 'Free' ? '#0d2b1a' : src.type === 'Paid' ? '#2a0d0d' : '#1a1808',
                color:  src.type === 'Free' ? '#2ea84b' : src.type === 'Paid' ? '#f44336' : '#f5a623',
              }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#e8f0f8' }}>{src.name}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#6a8aa8', mt: 0.2 }}>{src.detail}</Typography>
              </Box>
              <Chip label={src.live ? '● Live' : '○ Planned'} size="small" sx={{
                flexShrink: 0, fontSize: '0.58rem', height: 16,
                bgcolor: src.live ? '#0d2b1a' : '#1a2e44',
                color: src.live ? '#2ea84b' : 'rgba(245,166,35,0.4)',
              }} />
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', my: 4 }} />

        {/* Philosophy */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>🧠 Our Philosophy</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2 }}>
          {[
            { icon: '📊', title: 'Context Over Counting', body: 'Raw yards and TDs reward opportunity. We prioritize rate stats — EPA/play, success rate, snap share — that show how efficiently a player produces, not just volume.' },
            { icon: '🔓', title: 'Free First',            body: 'The best football analytics data (nflverse, ESPN) is already free. We build on the public data commons and only add paid tiers when the analysis genuinely demands it.' },
            { icon: '🎯', title: 'Explainable Rankings',  body: 'The Nerd Rating isn\'t a black box. Every weight, threshold, and formula component is documented. You can see exactly why your player ranked where they did.' },
          ].map(p => (
            <Card key={p.title} sx={{ bgcolor: 'rgba(10,20,34,0.6)', border: '1px solid #2a4560' }}>
              <CardContent>
                <Typography sx={{ fontSize: '1.4rem', mb: 0.75 }}>{p.icon}</Typography>
                <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.9rem' }}>{p.title}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#8aaac8', lineHeight: 1.6 }}>{p.body}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

      </Container>
    </Box>
  );
}
